import { useEffect, useState } from "react";
import type { ReactNode } from "react";

import { Button } from "../button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../card";
import { Input } from "../input";
import { Label } from "../label";
import { Separator } from "../separator";
import { DeleteAccountDialog } from "./delete-account-dialog";

export interface ProfilePageViewProps {
  email: string;
  isSaving: boolean;
  /**
   * Slot rendered in the CardFooter of the email card.
   * Pass a "Change email" button (web) or "Sign out" button (vault).
   * If omitted, no footer is rendered.
   */
  emailCardFooter?: ReactNode;
  /**
   * If provided, renders the danger-zone card with a hold-to-confirm delete dialog.
   */
  onDeleteAccount?: () => void;
  isDeleting?: boolean;

  /**
   * Full-name mode — a single "Full name" field backed by `auth.users.full_name`.
   * Used by the advisor app (`apps/app`), whose users have no `client.clients`
   * record. Provide `fullName` + `onSave`.
   */
  fullName?: string;
  onSave?: (fullName: string) => void;

  /**
   * Split-name mode — separate first/last fields backed by the owner's
   * `client.clients` first_name/last_name (the columns the advisor client list
   * reads), so an owner's rename actually reaches the advisor. Used by the vault
   * app (`apps/vault`). Provide `firstName` + `lastName` + `onSaveName`; when
   * `onSaveName` is set it takes precedence over `onSave`.
   */
  firstName?: string;
  lastName?: string;
  onSaveName?: (firstName: string, lastName: string) => void;
}

export function ProfilePageView({
  email,
  isSaving,
  emailCardFooter,
  onDeleteAccount,
  isDeleting = false,
  fullName,
  onSave,
  firstName,
  lastName,
  onSaveName,
}: ProfilePageViewProps) {
  const splitMode = onSaveName !== undefined;

  const [fullNameValue, setFullNameValue] = useState(fullName ?? "");
  const [firstNameValue, setFirstNameValue] = useState(firstName ?? "");
  const [lastNameValue, setLastNameValue] = useState(lastName ?? "");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    setFullNameValue(fullName ?? "");
    setFirstNameValue(firstName ?? "");
    setLastNameValue(lastName ?? "");
  }, [fullName, firstName, lastName]);

  const handleSaveName = () => {
    if (onSaveName) {
      onSaveName(firstNameValue.trim(), lastNameValue.trim());
    } else if (onSave) {
      onSave(fullNameValue);
    }
  };

  const saveDisabled =
    isSaving ||
    (splitMode && (!firstNameValue.trim() || !lastNameValue.trim()));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Profile</h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Manage your personal account settings
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Personal information</CardTitle>
          <CardDescription>Update your display name.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {splitMode ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="first-name">First name</Label>
                <Input
                  id="first-name"
                  value={firstNameValue}
                  onChange={(e) => setFirstNameValue(e.target.value)}
                  placeholder="Enter your first name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last-name">Last name</Label>
                <Input
                  id="last-name"
                  value={lastNameValue}
                  onChange={(e) => setLastNameValue(e.target.value)}
                  placeholder="Enter your last name"
                />
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="full-name">Full name</Label>
              <Input
                id="full-name"
                value={fullNameValue}
                onChange={(e) => setFullNameValue(e.target.value)}
                placeholder="Enter your full name"
              />
            </div>
          )}
          <div className="flex justify-end">
            <Button disabled={saveDisabled} onClick={handleSaveName}>
              Save changes
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Account email</CardTitle>
          <CardDescription>
            The email address associated with your account.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label htmlFor="email">Email address</Label>
            <Input
              id="email"
              type="email"
              value={email}
              readOnly
              className="bg-muted"
            />
          </div>
        </CardContent>
        {emailCardFooter && (
          <CardFooter className="flex justify-end">
            {emailCardFooter}
          </CardFooter>
        )}
      </Card>

      {onDeleteAccount && (
        <>
          <Separator />
          <Card className="border-red-200">
            <CardHeader>
              <CardTitle className="text-base text-red-700">
                Danger zone
              </CardTitle>
              <CardDescription>
                Permanently delete your account and all of its data. This action
                cannot be undone.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                variant="outline"
                className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                onClick={() => setDeleteDialogOpen(true)}
              >
                Delete account
              </Button>
            </CardContent>
          </Card>
          <DeleteAccountDialog
            open={deleteDialogOpen}
            onOpenChange={setDeleteDialogOpen}
            onConfirm={onDeleteAccount}
            isDeleting={isDeleting}
          />
        </>
      )}
    </div>
  );
}
